import React, { useEffect, useMemo, useRef, useState } from "react";
import "./OrdersListModal.css"; // używamy tych samych styli co inne nowe modale
import { API_URL } from "../config";

export default function AssignOrdersModal({ visible, onClose }) {
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [editingOrderId, setEditingOrderId] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [saving, setSaving] = useState(false);

  // Zbiorcze
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkUserId, setBulkUserId] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);

  // Wyszukiwanie / sortowanie
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState({ key: "nr_ref", dir: "asc" });
  const searchRef = useRef(null);

  useEffect(() => {
    if (!visible) return;
    fetchData();
    // autofocus
    setTimeout(() => searchRef.current?.focus(), 0);

    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  async function fetchData() {
    setError("");
    setLoading(true);
    try {
      // 1) Zlecenia
      const rOrders = await fetch(`${API_URL}/orders`, {
        credentials: "include",
      });
      if (!rOrders.ok) throw new Error("Nie udało się pobrać zleceń");
      const allOrders = await rOrders.json();

      // 2) Pracownicy – do selecta
      const rUsers = await fetch(`${API_URL}/orders/workers`, {
        credentials: "include",
      });
      if (!rUsers.ok) throw new Error("Nie udało się pobrać użytkowników");
      const workers = await rUsers.json();

      // 3) Filtr: brak wykonawcy lub wykonawca o id = 29
      const filtered = (allOrders || []).filter((o) => {
        const execId = o.osoba_wykonujaca_id == null ? null : Number(o.osoba_wykonujaca_id);
        return execId === null || execId === 29;
      });

      setOrders(filtered);
      setUsers(workers);
      setSelectedIds(new Set()); // wyczyść zaznaczenia po odświeżeniu
    } catch (err) {
      setError(err.message || "Błąd podczas pobierania danych");
    } finally {
      setLoading(false);
    }
  }

  const filteredSorted = useMemo(() => {
    const q = query.trim().toLowerCase();
    let data = !q
      ? orders
      : orders.filter((o) => {
          const byRef = (o.nr_ref || "").toLowerCase().includes(q);
          const byTitle = (o.tytul || "").toLowerCase().includes(q);
          const byType = (o.rodzaj_zgloszenia || "").toLowerCase().includes(q);
          const byReporter = (`${o.first_name || ""} ${o.last_name || ""}`.trim())
            .toLowerCase()
            .includes(q);
          const byClient = (o.client_name || "").toLowerCase().includes(q);
          return byRef || byTitle || byType || byReporter || byClient;
        });

    if (!sort.key) return data;

    const dir = sort.dir === "asc" ? 1 : -1;
    return [...data].sort((a, b) => {
      const key = sort.key;
      let av = a[key];
      let bv = b[key];

      if (key === "deadline" || key === "created_at") {
        av = av ? new Date(av).getTime() : 0;
        bv = bv ? new Date(bv).getTime() : 0;
      } else {
        av = (av ?? "").toString().toLowerCase();
        bv = (bv ?? "").toString().toLowerCase();
      }
      if (av < bv) return -1 * dir;
      if (av > bv) return  1 * dir;
      return 0;
    });
  }, [orders, query, sort]);

  function toggleSort(key) {
    setSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }
    );
  }

  // Jedno zlecenie
  function startAssign(orderId, currentExecId) {
    setEditingOrderId(orderId);
    setSelectedUserId(currentExecId ? String(currentExecId) : "");
  }
  function cancelAssign() {
    setEditingOrderId(null);
    setSelectedUserId("");
  }
  async function saveAssign(orderId) {
    if (!selectedUserId) return;
    setSaving(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/orders/${orderId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ osoba_wykonujaca_id: Number(selectedUserId) }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.error || "Nie udało się zapisać zmian");
      }
      await fetchData();
      cancelAssign();
    } catch (err) {
      setError(err.message || "Błąd zapisu");
    } finally {
      setSaving(false);
    }
  }

  // Zbiorcze
  function toggleSelect(id) {
    setSelectedIds((prev) => {
      const copy = new Set(prev);
      copy.has(id) ? copy.delete(id) : copy.add(id);
      return copy;
    });
  }
  function toggleSelectAll() {
    setSelectedIds((prev) => {
      const allShown = filteredSorted.map((o) => o.id);
      if (prev.size === allShown.length) return new Set(); // odznacz wszystkie
      return new Set(allShown);
    });
  }
  async function bulkAssign() {
    if (!bulkUserId || selectedIds.size === 0) return;
    setBulkSaving(true);
    setError("");
    try {
      const ids = Array.from(selectedIds);
      const body = (id) => ({
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ osoba_wykonujaca_id: Number(bulkUserId) }),
      });
      const results = await Promise.allSettled(
        ids.map((id) => fetch(`${API_URL}/orders/${id}`, body(id)))
      );
      const failed = results.filter((r) => r.status === "rejected" || (r.value && !r.value.ok));
      if (failed.length) {
        setError(`Przydzielono: ${ids.length - failed.length}, błędy: ${failed.length}.`);
      }
      await fetchData();
      setSelectedIds(new Set());
    } catch (err) {
      setError(err.message || "Błąd zbiorczego przydzielania");
    } finally {
      setBulkSaving(false);
    }
  }

  if (!visible) return null;

  return (
    <div
      className="orders-modal__overlay"
      onMouseDown={(e) => e.target.classList.contains("orders-modal__overlay") && onClose?.()}
      role="dialog"
      aria-modal="true"
    >
      <div className="orders-modal orders-modal--xl">
        {/* HEADER */}
        <header className="orders-modal__header">
          <h3 className="orders-modal__title">
            Przydzielanie Zleceń
            <span className="orders-modal__badge">{orders.length}</span>
          </h3>
          <button className="orders-modal__close-x" onClick={onClose} aria-label="Zamknij">×</button>
        </header>

        {/* TOOLS */}
        <div className="assign-toolbar">
          <div className="assign-toolbar__left">
            <input
              ref={searchRef}
              className="orders-modal__search"
              placeholder="Szukaj po nr, tytule, rodzaju, zgłaszającym lub kliencie…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <button className="btn btn--ghost" onClick={fetchData} title="Odśwież">Odśwież</button>
          </div>
          <div className="assign-toolbar__right">
            <select
              className="assign-select"
              value={bulkUserId}
              onChange={(e) => setBulkUserId(e.target.value)}
            >
              <option value="">— wybierz osobę —</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.first_name} {u.last_name}
                </option>
              ))}
            </select>
            <button
              className="btn btn--primary"
              onClick={bulkAssign}
              disabled={!bulkUserId || selectedIds.size === 0 || bulkSaving}
              title="Przydziel zaznaczone"
            >
              Przydziel zaznaczone ({selectedIds.size})
            </button>
          </div>
        </div>

        {/* ERROR */}
        {error && (
          <div className="assign-error">❌ {error}</div>
        )}

        {/* LISTA */}
        <div className="orders-modal__list" style={{ padding: 0 }}>
          {loading ? (
            <div className="orders-modal__empty">Ładowanie…</div>
          ) : filteredSorted.length === 0 ? (
            <div className="orders-modal__empty">Brak zleceń do przydzielenia.</div>
          ) : (
            <div className="assign-table__wrapper">
              <table className="assign-table">
                <thead>
                  <tr>
                    <th className="w-min">
                      <input
                        type="checkbox"
                        checked={selectedIds.size === filteredSorted.length && filteredSorted.length > 0}
                        onChange={toggleSelectAll}
                      />
                    </th>
                    <SortableTh label="Nr Zlecenia"  k="nr_ref"      sort={sort} onSort={toggleSort} />
                    <SortableTh label="Tytuł"        k="tytul"       sort={sort} onSort={toggleSort} />
                    <SortableTh label="Rodzaj"       k="rodzaj_zgloszenia" sort={sort} onSort={toggleSort} />
                    <SortableTh label="Zgłaszający"  k="reporter"    sort={sort} onSort={toggleSort} />
                    <SortableTh label="Klient"       k="client_name" sort={sort} onSort={toggleSort} />
                    <SortableTh label="Termin"       k="deadline"    sort={sort} onSort={toggleSort} />
                    <th className="w-min">Akcja</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSorted.map((o) => {
                    const reporter = `${o.first_name || ""} ${o.last_name || ""}`.trim();
                    const checked = selectedIds.has(o.id);
                    return (
                      <tr key={o.id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleSelect(o.id)}
                          />
                        </td>
                        <td className="mono">{o.nr_ref || "—"}</td>
                        <td className="ellipsis" title={o.tytul || ""}>{o.tytul || "—"}</td>
                        <td>{o.rodzaj_zgloszenia || "—"}</td>
                        <td>{reporter || "—"}</td>
                        <td>{o.client_name || "—"}</td>
                        <td className="mono">{o.deadline ? formatDate(o.deadline) : "—"}</td>
                        <td className="nowrap">
                          {editingOrderId === o.id ? (
                            <div className="row-actions">
                              <select
                                className="assign-select"
                                value={selectedUserId}
                                onChange={(e) => setSelectedUserId(e.target.value)}
                              >
                                <option value="">— wybierz osobę —</option>
                                {users.map((u) => (
                                  <option key={u.id} value={u.id}>
                                    {u.first_name} {u.last_name}
                                  </option>
                                ))}
                              </select>
                              <button
                                className="btn btn--primary"
                                onClick={() => saveAssign(o.id)}
                                disabled={!selectedUserId || saving}
                              >
                                Zapisz
                              </button>
                              <button
                                className="btn btn--ghost"
                                onClick={cancelAssign}
                                disabled={saving}
                              >
                                Anuluj
                              </button>
                            </div>
                          ) : (
                            <button
                              className="btn btn--primary"
                              onClick={() => startAssign(o.id, o.osoba_wykonujaca_id)}
                            >
                              Przydziel
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* FOOTER */}
        <footer className="orders-modal__footer">
          <button className="btn btn--secondary" onClick={onClose}>Zamknij</button>
        </footer>
      </div>
    </div>
  );
}

function SortableTh({ label, k, sort, onSort }) {
  const active = sort.key === k;
  const arrow = active ? (sort.dir === "asc" ? "▲" : "▼") : "";
  return (
    <th>
      <button className="sort-btn" onClick={() => onSort(k)}>
        {label} <span className="sort-arrow">{arrow}</span>
      </button>
    </th>
  );
}

function formatDate(dateString) {
  if (!dateString) return "—";
  const d = new Date(dateString);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
}
